import { usePrograms } from '@/hooks/use-programs';
import { routes } from '@/utils/routes';
import { DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import {
    MediaPack,
    MediaPackGuide,
    MediaPackProgram,
} from '@calm/evexia-web-sdk';
import {
    PackPageSkeleton,
    PlaceholderWhileOffScreen,
} from '@rhg/components/placeholder-while-offscreen';
import { MaxWidthLayout } from '@rhg/components/ui/layout';
import { useActiveCircleId } from '@rhg/hooks/use-active-circle';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { usePacks } from '@rhg/hooks/use-packs';
import { Box } from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { getViewAllHrefFromPackClass } from '@rhg/utils/pack-card';
import {
    collectPackClassesFromPack,
    collectProgramIdsFromPack,
} from '@rhg/utils/pack-program';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { dailyPracticeCardWidth } from '../discover-dailies/daily-packs';
import { PackGuideCard } from '../pack-guide-card';
import { PackProgramCard } from '../pack-program-card';
import { FullWidthLinearProgress } from '../ui/progress';
import { PackDetailsLayout, PackDetailsPaddedContainer } from './layout';
import { PackCarouselSection } from './pack-carousel-section';
import { PackGridItems } from './pack-grid-items';
import { PackPageGridSection } from './pack-page-grid-section';

const styleFlexGrid = {
    display: 'flex',
    flexWrap: 'wrap',
};
const styleFlexGap = {
    columnGap: DS_SPACING_UNITS.CONTENT,
    rowGap: DS_SPACING_UNITS.SECTION,
};
const styleMobileGap = {
    gap: DS_SPACING_UNITS.SIDE_SMALL,
};
const styleCenterGrid = {
    justifyContent: 'center',
};

export const styleCardFooter = {
    minHeight: 'auto',
};

interface PackPageProps {
    packClass?: string;
    viewAll?: string;
}

const stylePackCarouselList = {
    'li > div.MuiBox-root': {
        height: '100%',
    },
};

export const MAX_CENTERED_ITEMS = 5;
const oneByOneSingleColumnWidth = 164;

function PackPageWithPrograms({
    packClass,
    viewAll,
    pack,
}: {
    packClass: string;
    pack: MediaPack;
    viewAll?: string;
}) {
    // Called here so that we can pre-populate the programs cache
    const programIds = collectProgramIdsFromPack(pack);
    const { isFetched, isEnabled } = usePrograms([
        {
            packClass,
            programIds,
        },
    ]);

    // Called here so that we can pre-populate the packs cache
    const nestedPackClasses = collectPackClassesFromPack(pack);
    const { activeCircleId } = useActiveCircleId();
    const { isFetched: isFetchedPacks, isEnabled: isEnabledPacks } = usePacks(
        nestedPackClasses,
        activeCircleId
    );

    const [centerGridItems, setCenterGridItems] = useState(false);
    const isMobile = useIsMobile('md');

    // Default - account for program cards view all
    useEffect(() => {
        if (
            viewAll &&
            isFetched &&
            (pack.items?.length ?? Number.POSITIVE_INFINITY) <=
                MAX_CENTERED_ITEMS
        ) {
            setCenterGridItems(true);
        }
    }, [isFetched, pack, viewAll]);

    const isProgramsLoading = !isFetched && isEnabled;
    const isPacksLoading = !isFetchedPacks && isEnabledPacks;
    if (isProgramsLoading || isPacksLoading) {
        return <FullWidthLinearProgress />;
    }

    const isPackOfPacks = pack?.items?.[0].type === 'pack';
    const title = pack?.title ?? '';
    const subtitle = pack?.subtitle ?? '';
    const packItemsLength = pack?.items.length ?? 0;

    if (viewAll) {
        const viewAllCardWidth = isMobile
            ? oneByOneSingleColumnWidth
            : dailyPracticeCardWidth;
        return (
            <PackDetailsLayout
                title={title}
                subtitle={subtitle}
                packClass={packClass}
            >
                <MaxWidthLayout>
                    <PackDetailsPaddedContainer>
                        <Box
                            sx={sxAppend(
                                styleFlexGrid,
                                isMobile ? styleMobileGap : styleFlexGap,
                                centerGridItems && styleCenterGrid
                            )}
                        >
                            {pack?.items.map((packItem) => {
                                if (packItem?.type === 'pack') {
                                    return (
                                        <PackGridItems
                                            key={packItem.packClass}
                                            packClass={packItem.packClass}
                                            setCenterGridItems={
                                                setCenterGridItems
                                            }
                                        />
                                    );
                                } else if (packItem?.type === 'guide') {
                                    const guideItem =
                                        packItem as MediaPackGuide;
                                    return (
                                        <PlaceholderWhileOffScreen
                                            placeholder={<PackPageSkeleton />}
                                            key={packItem.programId}
                                        >
                                            <PackGuideCard
                                                packClass={
                                                    pack?.packClass ?? ''
                                                }
                                                variant="carouselSquare"
                                                packGuide={guideItem}
                                                sx={{
                                                    width: viewAllCardWidth,
                                                }}
                                                contentSx={styleCardFooter}
                                            />
                                        </PlaceholderWhileOffScreen>
                                    );
                                } else {
                                    const programItem =
                                        packItem as MediaPackProgram;
                                    return (
                                        <PlaceholderWhileOffScreen
                                            placeholder={<PackPageSkeleton />}
                                            key={packItem.programId}
                                        >
                                            <PackProgramCard
                                                hideImageOverlays
                                                packClass={
                                                    pack?.packClass ?? ''
                                                }
                                                variant="carouselSquare"
                                                packProgram={programItem}
                                                sx={{
                                                    width: viewAllCardWidth,
                                                }}
                                                contentSx={styleCardFooter}
                                            />
                                        </PlaceholderWhileOffScreen>
                                    );
                                }
                            })}
                        </Box>
                    </PackDetailsPaddedContainer>
                </MaxWidthLayout>
            </PackDetailsLayout>
        );
    }

    if (isPackOfPacks) {
        const isRowCard = pack?.displayType === 'row-card';

        return (
            <PackDetailsLayout
                title={title}
                subtitle={subtitle}
                packClass={packClass}
                imageData={isRowCard ? pack?.image : undefined}
            >
                {pack?.items.map((packItem, i) => {
                    if (packItem?.type === 'pack') {
                        const viewAllHref = getViewAllHrefFromPackClass({
                            displayType: packItem.displayType,
                            packClass: packItem.packClass,
                            isViewAll: true,
                        });

                        return (
                            <div
                                data-testid="PackPageSection"
                                key={packItem.packClass}
                            >
                                <PackCarouselSection
                                    packClass={packItem.packClass}
                                    title={packItem.title}
                                    cardVariant="carouselSquare"
                                    viewAllHref={viewAllHref}
                                    showDivider={
                                        isRowCard
                                            ? i !== packItemsLength - 1
                                            : true
                                    }
                                    packCarouselProps={{
                                        listSx: stylePackCarouselList,
                                    }}
                                />
                            </div>
                        );
                    }
                    return null;
                })}
            </PackDetailsLayout>
        );
    }

    return (
        <PackDetailsLayout
            title={title}
            subtitle={subtitle}
            packClass={packClass}
        >
            <PackPageGridSection pack={pack} />
        </PackDetailsLayout>
    );
}

export const PackPage = ({ packClass, viewAll }: PackPageProps) => {
    const { activeCircleId } = useActiveCircleId();
    const router = useRouter();
    const { isFetched, packs } = usePacks([packClass ?? ''], activeCircleId);
    const pack = packs?.[0];

    useEffect(() => {
        // redirect if no pack
        if (isFetched && (!pack || !packClass)) {
            router.push(routes.notFound);
        }
    }, [isFetched, pack, packClass, router]);

    if (!isFetched) {
        return <FullWidthLinearProgress />;
    }

    if (!pack || !packClass) {
        // This will be redirected
        return <FullWidthLinearProgress />;
    }

    return (
        <PackPageWithPrograms
            packClass={packClass}
            viewAll={viewAll}
            pack={pack}
        />
    );
};
